import { zodResolver } from '@hookform/resolvers/zod';
import orderBy from 'lodash/orderBy';
import range from 'lodash/range';
import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { SubstituteOperatingPeriodSettingsInfoFragment } from '../../../../generated/graphql';
import {
  setIsCommonSubstitutePeriodFormDirtyAction,
  useAppDispatch,
} from '../../../../redux';
import { DateRange } from '../../../../types';
import { isDateInRange, mapToISODate } from '../../../../utils/time';
import { SimpleButton } from '../../../common/Buttons';
import { submitFormByRef } from '../../../common/Forms';
import { useDirtyFormBlockNavigation } from '../../../common/Forms/NavigationBlocker';
import { Row } from '../../../common/LayoutComponents';
import { ConfirmationDialog } from '../../../common/Modals';
import { commonSubstituteDayData } from '../common_substitute_day_data';
import {
  mapDateTimeToFormState,
  mapLineTypes,
  parseSubstituteDayOfWeek,
} from '../utils';
import {
  CommonDayType,
  FormState,
  UpdateField,
  schema,
} from './CommonSubstitutePeriodForm.types';
import { CommonSubstitutePeriodItem } from './CommonSubstitutePeriodItem';

const testIds = {
  cancelButton: 'CommonSubstitutePeriodForm::cancelButton',
  saveButton: 'CommonSubstitutePeriodForm::saveButton',
};

type GeneratedDate = { name: string; date: Temporal.PlainDate };

function generatePresetDatesForDateRange(
  startDate: Temporal.PlainDate,
  endDate: Temporal.PlainDate,
): Array<GeneratedDate> {
  const years = range(startDate.year, endDate.year + 1);

  // Generate individual days for each year
  // return generated dates within date range
  return years
    .flatMap((year) =>
      commonSubstituteDayData.map<GeneratedDate>(({ name, month, day }) => ({
        name: `${name} ${year}`,
        date: Temporal.PlainDate.from({ year, month, day }),
      })),
    )
    .filter(({ date }) => isDateInRange(date, startDate, endDate));
}

export function mapCommonSubstituteOperatingPeriodsToCommonDays(
  commonSubstituteOperatingPeriods: ReadonlyArray<SubstituteOperatingPeriodSettingsInfoFragment>,
): CommonDayType[] {
  return commonSubstituteOperatingPeriods.map((period) => ({
    periodId: period.substitute_operating_period_id,
    periodName: period.period_name,
    supersededDate: mapDateTimeToFormState(
      period.substitute_operating_day_by_line_types[0].superseded_date,
    ),
    substituteDayOfWeek: parseSubstituteDayOfWeek(
      period.substitute_operating_day_by_line_types[0].substitute_day_of_week,
    ),
    lineTypes: mapLineTypes(period),
    fromDatabase: true,
    created: false,
    isPreset: period.is_preset,
  }));
}

function combineCommonDaysWithPresetDates(
  commonDays: ReadonlyArray<CommonDayType>,
  presetDays: ReadonlyArray<GeneratedDate>,
): ReadonlyArray<CommonDayType> {
  const displayedPresetDays: Array<CommonDayType> = presetDays
    .filter(
      (presetDay) =>
        !commonDays.some(
          (commonDay) =>
            presetDay.name === commonDay.periodName &&
            mapToISODate(presetDay.date) === commonDay.supersededDate,
        ),
    )
    .map((presetDay) => ({
      periodName: presetDay.name,
      supersededDate: mapDateTimeToFormState(presetDay.date),
      lineTypes: [],
      substituteDayOfWeek: '',
      fromDatabase: false,
      created: false,
      isPreset: true,
    }));

  return commonDays.concat(displayedPresetDays);
}

type CommonSubstitutePeriodFormProps = {
  readonly className?: string;
  readonly commonDays?: ReadonlyArray<CommonDayType>;
  readonly onSubmit: (state: FormState) => void;
  readonly dateRange: DateRange;
};

export const CommonSubstitutePeriodForm: FC<
  CommonSubstitutePeriodFormProps
> = ({
  className,
  commonDays,
  onSubmit,
  dateRange: { startDate, endDate },
}) => {
  const { t } = useTranslation();
  const formRef = useRef<ExplicitAny>(null);
  const dispatch = useAppDispatch();

  const [isResetting, setIsResetting] = useState<boolean>(false);

  const generatedPresetDates = useMemo(
    () => generatePresetDatesForDateRange(startDate, endDate),
    [startDate, endDate],
  );

  const orderedDisplayedCommonDays = useMemo(() => {
    const displayedCommonDays = combineCommonDaysWithPresetDates(
      commonDays ?? [],
      generatedPresetDates,
    );

    return orderBy(displayedCommonDays, (day) => day.supersededDate, ['asc']);
  }, [generatedPresetDates, commonDays]);

  const methods = useForm<FormState>({
    values: { commonDays: orderedDisplayedCommonDays },
    resolver: zodResolver(schema),
  });
  useDirtyFormBlockNavigation(methods.formState, 'CommonSubstitutePeriodForm');

  const {
    clearErrors,
    control,
    formState: { isDirty },
    handleSubmit,
    reset,
    resetField,
    setValue,
  } = methods;

  const { fields } = useFieldArray({
    control,
    name: 'commonDays',
  });

  const onReset = () => {
    setIsResetting(false);
    reset();
  };

  const onSave = () => {
    submitFormByRef(formRef);
  };

  useEffect(() => {
    dispatch(setIsCommonSubstitutePeriodFormDirtyAction(isDirty));
  }, [dispatch, isDirty]);

  const update = (index: number, value: boolean, field: UpdateField) => {
    // Clear errors from specific item only when "removing" it
    if (value) {
      clearErrors(`commonDays.${index}`);
    }

    if (!value) {
      setValue(`commonDays.${index}.${field}`, true, {
        shouldDirty: true,
        shouldTouch: true,
      });
    } else {
      resetField(`commonDays.${index}`);
    }
  };

  return (
    <div className={className}>
      <FormProvider {...methods}>
        <form ref={formRef} onSubmit={handleSubmit(onSubmit)}>
          <div className="flex flex-wrap justify-between gap-8">
            {fields.map((field, index) => (
              <CommonSubstitutePeriodItem
                field={field}
                index={index}
                key={field.id}
                update={update}
              />
            ))}
          </div>
          <Row className="my-8 justify-end gap-4">
            <SimpleButton
              onClick={() => setIsResetting(true)}
              disabled={!isDirty}
              id="cancel-button"
              testId={testIds.cancelButton}
              inverted
            >
              {t(($) => $.cancel)}
            </SimpleButton>

            <SimpleButton
              onClick={onSave}
              disabled={!isDirty}
              id="save-button"
              testId={testIds.saveButton}
            >
              {t(($) => $.save)}
            </SimpleButton>
          </Row>
        </form>
      </FormProvider>
      <ConfirmationDialog
        isOpen={isResetting}
        onCancel={() => setIsResetting(false)}
        onConfirm={onReset}
        title={t(($) => $.confirmResetCommonSubstitutePeriodDialog.title)}
        description={t(
          ($) => $.confirmResetCommonSubstitutePeriodDialog.description,
        )}
        confirmText={t(
          ($) => $.confirmResetCommonSubstitutePeriodDialog.confirmText,
        )}
        cancelText={t(
          ($) => $.confirmResetCommonSubstitutePeriodDialog.cancelText,
        )}
      />
    </div>
  );
};
